import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Empty, ExerciseThumb, Icon, Loading, Modal, useConfirm, useToast } from '../components/ui';
import { BusyButton } from '../components/nutrition';
import { dateKey, formatDate, parseDateKey } from '../lib/dates';
import { useExercises } from '../lib/hooks';
import {
  formatLiftSet, formatLiftWeight, liftBest, liftBestOneRepMax, liftDisplayName, liftHistories, liftRecordIndices, liftValue,
} from '../lib/lifts';
import { deleteBodyEntry, setBodyMetrics } from '../lib/services';
import { useBodyDays, useProgram, useProgressDays } from '../lib/useUserData';

/* ───────── Metric chart (port of the app's BodyMetricChartCard) ───────── */
const CHART_H = 190;
const AXIS_W = 36;
const TOP_PAD = 14;
const BOTTOM_LABEL_H = 22;
const SIDE_PAD = 8;
const MAX_DOTTED = 16;

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** 0 → 1 over `ms` with easeOutCubic, starting once `run` turns true. */
function useReveal(run, ms = 900) {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (!run) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const x = Math.min(1, (now - start) / ms);
      setP(1 - (1 - x) ** 3);
      if (x < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, ms]);
  return p;
}

function niceStep(raw) {
  if (raw <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const f = raw / mag;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * mag;
}

/** Monotone cubic (Fritsch–Carlson): smooth, never overshoots the real values. */
function smoothPath(p) {
  const n = p.length;
  let d = `M${p[0][0]},${p[0][1]}`;
  if (n < 3) {
    for (let i = 1; i < n; i++) d += ` L${p[i][0]},${p[i][1]}`;
    return d;
  }
  const dx = [];
  const s = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(p[i + 1][0] - p[i][0]);
    s.push(dx[i] === 0 ? 0 : (p[i + 1][1] - p[i][1]) / dx[i]);
  }
  const m = new Array(n).fill(0);
  m[0] = s[0];
  m[n - 1] = s[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (s[i - 1] * s[i] <= 0) m[i] = 0;
    else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      m[i] = (w1 + w2) / (w1 / s[i - 1] + w2 / s[i]);
    }
  }
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    if (h === 0) {
      d += ` L${p[i + 1][0]},${p[i + 1][1]}`;
      continue;
    }
    d += ` C${p[i][0] + h / 3},${p[i][1] + (m[i] * h) / 3} ${p[i + 1][0] - h / 3},${p[i + 1][1] - (m[i + 1] * h) / 3} ${p[i + 1][0]},${p[i + 1][1]}`;
  }
  return d;
}

const two = (n) => String(n).padStart(2, '0');
const chartDate = (d, withYear = true) => `${two(d.getDate())}.${two(d.getMonth() + 1)}${withYear ? `.${d.getFullYear()}` : ''}`;

function MetricChart({ points, color, width, selected, onSelect }) {
  const reveal = useReveal(width > 0);
  const uid = useId().replace(/:/g, '');
  const scrubbing = useRef(false);
  if (!width) return <div style={{ height: CHART_H }} />;

  const plot = { left: SIDE_PAD, top: TOP_PAD, right: width - AXIS_W, bottom: CHART_H - BOTTOM_LABEL_H };
  const pw = plot.right - plot.left;
  const ph = plot.bottom - plot.top;
  // Evenly spaced x positions: time-proportional spacing turned records entered
  // minutes apart into near-vertical jumps.
  const xs = points.length === 1 ? [plot.left + pw / 2] : points.map((_, i) => plot.left + (pw * i) / (points.length - 1));

  // Y domain rounded to "nice" grid steps with a little headroom.
  let lo = Math.min(...points.map((p) => p.v));
  let hi = Math.max(...points.map((p) => p.v));
  const minSpan = Math.max(1, Math.abs(hi) * 0.02);
  if (hi - lo < minSpan) {
    const mid = (hi + lo) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  const step = niceStep((hi - lo) / 3);
  const axisMin = Math.floor(lo / step) * step;
  const axisMax = Math.ceil(hi / step) * step;
  const yFor = (v) => plot.bottom - ((v - axisMin) / (axisMax - axisMin)) * ph;
  const ticks = [];
  for (let v = axisMin; v <= axisMax + step / 2; v += step) ticks.push(v);
  const decimals = step < 1 ? 1 : 0;

  const offsets = points.map((p, i) => [xs[i], yFor(p.v)]);
  const line = offsets.length >= 2 ? smoothPath(offsets) : null;
  const topY = Math.min(...offsets.map((o) => o[1]));

  const nearest = (e) => {
    const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
    let best = 0;
    for (let i = 1; i < xs.length; i++) if (Math.abs(xs[i] - x) < Math.abs(xs[best] - x)) best = i;
    return best;
  };
  // Mouse: hover scrubs, leaving clears. Touch: tap toggles, drag scrubs and
  // releasing clears — like the app.
  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse') return;
    const i = nearest(e);
    scrubbing.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onSelect(i === selected ? null : i);
  };
  const onPointerMove = (e) => {
    if (e.pointerType === 'mouse') return onSelect(nearest(e));
    if (e.buttons || e.pressure > 0) {
      scrubbing.current = true;
      onSelect(nearest(e));
    }
    return undefined;
  };
  const onPointerUp = (e) => {
    if (e.pointerType !== 'mouse' && scrubbing.current) {
      scrubbing.current = false;
      onSelect(null);
    }
  };
  const onPointerLeave = (e) => {
    if (e.pointerType === 'mouse') onSelect(null);
  };

  const midIndex = points.length >= 3 && pw > 220 ? Math.floor((points.length - 1) / 2) : null;
  const highlight = (o) => (
    <g>
      <circle cx={o[0]} cy={o[1]} r="11" style={{ fill: color, fillOpacity: 0.18 }} />
      <circle cx={o[0]} cy={o[1]} r="6.5" style={{ fill: 'var(--gm-bg)' }} />
      <circle cx={o[0]} cy={o[1]} r="4.5" style={{ fill: color }} />
    </g>
  );
  const sel = selected != null ? offsets[selected] : null;

  return (
    <svg
      className="gm-metric-chart"
      width={width}
      height={CHART_H}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerLeave}
    >
      <defs>
        <linearGradient id={`${uid}a`} gradientUnits="userSpaceOnUse" x1="0" x2="0" y1={topY} y2={plot.bottom}>
          <stop offset="0" style={{ stopColor: color, stopOpacity: 0.32 }} />
          <stop offset="0.55" style={{ stopColor: color, stopOpacity: 0.1 }} />
          <stop offset="1" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
        <filter id={`${uid}g`} x="-10%" y="-50%" width="120%" height="200%">
          <feGaussianBlur stdDeviation="2" />
        </filter>
        <clipPath id={`${uid}c`}>
          <rect x="0" y="0" width={plot.left + (pw + SIDE_PAD) * reveal + 8} height={CHART_H} />
        </clipPath>
      </defs>

      {ticks.map((v) => (
        <g key={v}>
          <line className="gm-metric-chart__grid" x1={plot.left} x2={plot.right} y1={yFor(v)} y2={yFor(v)} />
          <text x={width} y={yFor(v)} textAnchor="end" dominantBaseline="central">{v.toFixed(decimals)}</text>
        </g>
      ))}

      {points.length === 1 ? (
        <text x={xs[0]} y={plot.bottom + 16} textAnchor="middle">{chartDate(points[0].date, false)}</text>
      ) : (
        <>
          <text x={plot.left} y={plot.bottom + 16}>{chartDate(points[0].date, false)}</text>
          <text x={plot.right} y={plot.bottom + 16} textAnchor="end">{chartDate(points[points.length - 1].date, false)}</text>
          {midIndex != null && <text x={xs[midIndex]} y={plot.bottom + 16} textAnchor="middle">{chartDate(points[midIndex].date, false)}</text>}
        </>
      )}

      <g clipPath={`url(#${uid}c)`}>
        {line && (
          <>
            <path d={`${line} L${offsets[offsets.length - 1][0]},${plot.bottom} L${offsets[0][0]},${plot.bottom} Z`} fill={`url(#${uid}a)`} />
            <path d={line} filter={`url(#${uid}g)`} style={{ fill: 'none', stroke: color, strokeOpacity: 0.25, strokeWidth: 6, strokeLinecap: 'round' }} />
            <path d={line} style={{ fill: 'none', stroke: color, strokeWidth: 3, strokeLinecap: 'round', strokeLinejoin: 'round' }} />
          </>
        )}
        {points.length <= MAX_DOTTED &&
          offsets.slice(0, -1).map((o, i) => (
            <circle key={points[i].key} cx={o[0]} cy={o[1]} r="3.5" style={{ fill: 'var(--gm-bg)', stroke: color, strokeWidth: 2 }} />
          ))}
      </g>

      {reveal > 0.95 && !sel && highlight(offsets[offsets.length - 1])}
      {sel && (
        <>
          <line className="gm-metric-chart__guide" x1={sel[0]} x2={sel[0]} y1={plot.top - 6} y2={plot.bottom} />
          {highlight(sel)}
        </>
      )}
    </svg>
  );
}

/** Either `items` + `field` (body records) or ready `points` ({ key, v, date }). */
function MetricCard({ title, items, field, points: given, unit, color, decimals = 1 }) {
  const t = useT();
  const [ref, width] = useWidth();
  const [selected, setSelected] = useState(null);
  const points = useMemo(
    () => given || items.filter((d) => d[field] != null).map((d) => ({ key: d.id, v: Number(d[field]), date: parseDateKey(d.id) })),
    [given, items, field],
  );
  const sel = selected != null && selected < points.length ? selected : null;
  const shown = sel != null ? points[sel] : points[points.length - 1] || null;
  let subtitle = '';
  if (sel != null) subtitle = chartDate(shown.date);
  else if (points.length >= 2) subtitle = `${chartDate(points[0].date)} – ${chartDate(points[points.length - 1].date)}`;
  else if (points.length === 1) subtitle = chartDate(points[0].date);
  // Change from the first record up to the shown point.
  const delta = points.length >= 2 && shown ? shown.v - points[0].v : null;
  const flat = delta != null && Math.abs(delta) < 0.05;

  return (
    <div className="gm-card gm-metric-card" style={{ '--metric-color': color }}>
      <div className="gm-metric-card__head">
        <div style={{ minWidth: 0 }}>
          <div className="gm-metric-card__title">{t.upper(title)}</div>
          <div className="gm-metric-card__value">
            {shown ? shown.v.toFixed(decimals) : '—'}
            {shown && <span> {unit}</span>}
          </div>
          {subtitle && <div className="gm-metric-card__sub">{subtitle}</div>}
        </div>
        {delta != null && (
          <span className="gm-metric-card__delta">
            <Icon name={flat ? 'trending_flat' : delta < 0 ? 'trending_down' : 'trending_up'} size={16} />
            {flat ? '' : delta < 0 ? '−' : '+'}{Math.abs(delta).toFixed(decimals)} {unit}
          </span>
        )}
      </div>
      <div ref={ref} className="gm-metric-card__chart">
        {points.length ? (
          <MetricChart points={points} color={color} width={width} selected={sel} onSelect={setSelected} />
        ) : (
          <div className="gm-metric-card__empty">
            <Icon name="show_chart" size={40} />
            <div>{t('noProgressData')}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────── Strength progress (port of the app's StrengthProgressSection) ───────── */
const COLLAPSED_LIFTS = 6;

function LiftDetailModal({ lift, onClose }) {
  const t = useT();
  const { history: h, name } = lift;
  const best = liftBest(h);
  const oneRepMax = liftBestOneRepMax(h);
  const records = liftRecordIndices(h);
  const points = h.entries.map((e) => ({ key: e.key, v: liftValue(h, e), date: e.date }));
  const stats = [
    [t('liftBestSetLabel'), formatLiftSet(t, best.weight, best.reps)],
    ...(oneRepMax != null ? [[t('liftOneRepMaxLabel'), `${formatLiftWeight(oneRepMax)} kg`]] : []),
    [t('liftSessionsLabel'), String(h.entries.length)],
  ];
  return (
    <Modal open onClose={onClose} title={name}>
      <div className="gm-stack">
        <MetricCard
          title={h.weighted ? t('liftTopSetChartTitle') : t('liftMostRepsChartTitle')}
          points={points}
          unit={h.weighted ? 'kg' : t('repsUnit')}
          decimals={h.weighted ? 1 : 0}
          color="var(--gm-primary)"
        />
        <div className="gm-lift-stats">
          {stats.map(([label, value]) => (
            <div key={label} className="gm-card gm-lift-stat">
              <div className="gm-lift-stat__value">{value}</div>
              <div className="gm-lift-stat__label">{label}</div>
            </div>
          ))}
        </div>
        <div>
          <div className="gm-upper-sm" style={{ margin: '4px 0 8px' }}>{t.upper(t('liftHistoryTitle'))}</div>
          <div className="gm-stack-sm">
            {h.entries.map((e, i) => ({ e, i })).reverse().map(({ e, i }) => {
              const sets = e.sets.filter((s) => s.reps > 0);
              const date = chartDate(e.date);
              return (
                <div key={e.key} className={`gm-card gm-lift-row${records.has(i) ? ' record' : ''}`}>
                  <div className="gm-grow" style={{ minWidth: 0 }}>
                    <div className="gm-lift-row__set">{formatLiftSet(t, e.weight, e.reps)}</div>
                    <div className="gm-tiny gm-muted">
                      {sets.length > 1
                        ? `${date}  ·  ${sets.map((s) => (s.weight == null ? `${s.reps}` : `${s.reps}×${formatLiftWeight(s.weight)}`)).join('  ')}`
                        : date}
                    </div>
                  </div>
                  {records.has(i) && <span className="gm-lift-tag">🏆 {t('liftRecordTag')}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function StrengthProgress({ uid, readOnly, programId }) {
  const t = useT();
  const { byId } = useExercises();
  const { progressDays, loading, error } = useProgressDays(uid, { live: !readOnly });
  const { program } = useProgram(readOnly ? null : programId);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(null);

  // Custom exercise names from the program, for workouts saved before names
  // were stored with them.
  const programNames = useMemo(() => {
    const names = {};
    for (const day of Object.values(program || {})) for (const e of day || []) if (e.name) names[e.exerciseId] = e.name;
    return names;
  }, [program]);

  const lifts = useMemo(
    () => liftHistories(progressDays)
      .filter((h) => (byId[h.exerciseId]?.muscle || '').trim().toLowerCase() !== 'cardio')
      .sort((a, b) => b.entries[b.entries.length - 1].date - a.entries[a.entries.length - 1].date),
    [progressDays, byId],
  );

  if (loading || error) return null;
  const shown = expanded || lifts.length <= COLLAPSED_LIFTS ? lifts : lifts.slice(0, COLLAPSED_LIFTS);

  return (
    <div>
      <div className="gm-upper-sm" style={{ margin: '8px 0' }}>{t.upper(t('strengthProgressTitle'))}</div>
      {lifts.length === 0 ? (
        <div className="gm-card gm-list-item">
          <Icon name="fitness_center" style={{ color: 'color-mix(in srgb, var(--gm-primary) 60%, transparent)' }} />
          <span className="gm-muted">{t('strengthProgressEmpty')}</span>
        </div>
      ) : (
        <>
          <div className="gm-lift-grid">
            {shown.map((h) => {
              const exercise = byId[h.exerciseId];
              const name = liftDisplayName(h.exerciseId, { exercise, customName: h.name || programNames[h.exerciseId], upper: t.upper });
              const best = liftBest(h);
              const first = h.entries[0];
              const last = h.entries[h.entries.length - 1];
              const delta = h.entries.length >= 2 ? liftValue(h, last) - liftValue(h, first) : null;
              return (
                <button key={h.exerciseId} type="button" className="gm-card gm-lift-tile" onClick={() => setOpen({ history: h, name })}>
                  <ExerciseThumb exercise={exercise} />
                  <span className="gm-grow" style={{ minWidth: 0 }}>
                    <span className="gm-lift-tile__name">{name}</span>
                    <span className="gm-lift-tile__sub">
                      {t('liftBestLabel', formatLiftSet(t, best.weight, best.reps))}  ·  {t('liftSessionCount', h.entries.length)}
                    </span>
                  </span>
                  {delta != null && delta > 0 && (
                    <span className="gm-lift-tag">{h.weighted ? `+${formatLiftWeight(delta)} kg` : `+${Math.round(delta)}`}</span>
                  )}
                  <Icon name="chevron_right" className="gm-muted" />
                </button>
              );
            })}
          </div>
          {lifts.length > COLLAPSED_LIFTS && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" className="gm-btn gm-btn--ghost" onClick={() => setExpanded((x) => !x)}>
                {expanded ? t('strengthShowLess') : t('strengthShowAll')}
              </button>
            </div>
          )}
        </>
      )}
      {open && <LiftDetailModal lift={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/* ───────── Entry modal ───────── */
export function BodyEntryModal({ uid, entry, onClose }) {
  const t = useT();
  const toast = useToast();
  const [date, setDate] = useState(entry ? entry.id.split('T')[0] : dateKey());
  const [w, setW] = useState(entry?.weight_kg != null ? String(entry.weight_kg) : '');
  const [f, setF] = useState(entry?.body_fat_percent != null ? String(entry.body_fat_percent) : '');
  const [busy, setBusy] = useState(false);
  const num = (s) => (String(s).trim() === '' ? null : parseFloat(String(s).replace(',', '.')));
  const save = async () => {
    const wk = num(w);
    const bf = num(f);
    if (w && (wk == null || Number.isNaN(wk) || wk < 20 || wk > 400)) return toast.warning(t('invalidWeight'));
    if (f && (bf == null || Number.isNaN(bf) || bf < 0 || bf > 70)) return toast.warning(t('invalidBodyFat'));
    if (wk == null && bf == null) return toast.warning(t('invalidWeight'));
    setBusy(true);
    try {
      let when = new Date();
      if (!entry && date !== dateKey()) {
        const d = parseDateKey(date);
        when = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
      }
      await setBodyMetrics(uid, when, wk, bf, entry?.id);
      toast.success(t('progressSaved'));
      onClose();
    } catch (e) {
      toast.error(t('progressSaveFailed'));
    } finally {
      setBusy(false);
    }
    return null;
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={entry ? t('editRecording') : t('bodyProgressTitle')}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose}>{t('cancel')}</button>
          <BusyButton busy={busy} onClick={save}>{busy ? t('savingProgress') : t('save')}</BusyButton>
        </>
      )}
    >
      <div className="gm-stack">
        {!entry && (
          <div className="gm-field">
            <label>{t('date')}</label>
            <input className="gm-input" type="date" value={date} max={dateKey()} onChange={(e) => setDate(e.target.value || dateKey())} />
          </div>
        )}
        <div className="gm-grid-2 keep">
          <div className="gm-field"><label>{t('weight')} (kg)</label><input className="gm-input" inputMode="decimal" value={w} onChange={(e) => setW(e.target.value)} autoFocus /></div>
          <div className="gm-field"><label>{t('bodyFat')} (%)</label><input className="gm-input" inputMode="decimal" value={f} onChange={(e) => setF(e.target.value)} /></div>
        </div>
      </div>
    </Modal>
  );
}

export function BodyRecordsList({ uid, items, readOnly }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [edit, setEdit] = useState(null);
  if (!items.length) return <Empty icon="monitor_weight">{t('noRecords')}</Empty>;
  const remove = async (d) => {
    const ok = await confirm({ title: t('deleteRecording'), message: t('deleteRecordingConfirm'), confirmLabel: t('deleteEntry'), danger: true });
    if (!ok) return;
    try {
      await deleteBodyEntry(uid, d.id);
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    }
  };
  return (
    <div className="gm-card gm-card--flush">
      {[...items].reverse().map((d) => {
        const dt = new Date(d.id.length > 10 ? d.id : `${d.id}T00:00:00`);
        return (
          <div key={d.id} className="gm-list-item">
            <div className="gm-icon-tile gm-icon-tile--sm"><Icon name="monitor_weight" /></div>
            <div className="gm-grow">
              <div className="gm-strong">
                {d.weight_kg != null ? `${Number(d.weight_kg).toFixed(1)} kg` : '—'}
                <span className="gm-muted" style={{ fontWeight: 500 }}> · {d.body_fat_percent != null ? `${Number(d.body_fat_percent).toFixed(1)} %` : '—'}</span>
              </div>
              <div className="gm-tiny gm-muted">{formatDate(dt, t.locale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            {!readOnly && (
              <>
                <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => setEdit(d)} title={t('edit')}><Icon name="edit" outline /></button>
                <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => remove(d)} title={t('deleteEntry')}><Icon name="delete" outline /></button>
              </>
            )}
          </div>
        );
      })}
      {edit && <BodyEntryModal uid={uid} entry={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

export function ProgressView({ uid, readOnly, showRecords, programId }) {
  const t = useT();
  const { bodyDays, loading, error } = useBodyDays(uid, { live: !readOnly });
  const items = useMemo(() => bodyDays, [bodyDays]);
  if (loading) return <Loading />;
  if (error) return <Empty icon="lock">{t('errorLoadingData')}</Empty>;
  return (
    <div className="gm-stack">
      <div className="gm-grid-2">
        <MetricCard title={t('weight')} items={items} field="weight_kg" unit="kg" color="var(--gm-primary)" />
        <MetricCard title={t('bodyFat')} items={items} field="body_fat_percent" unit="%" color="var(--gm-primary)" />
      </div>
      <StrengthProgress uid={uid} readOnly={readOnly} programId={programId} />
      {showRecords && (
        <div>
          <div className="gm-upper-sm" style={{ margin: '8px 0' }}>{t.upper(t('allRecords'))}</div>
          <BodyRecordsList uid={uid} items={items} readOnly={readOnly} />
        </div>
      )}
    </div>
  );
}

export default function Progress() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const [adding, setAdding] = useState(false);
  return (
    <div className="gm-content gm-enter" style={{ paddingBottom: 160 }}>
      <div className="gm-page-head">
        <h1 className="gm-page-title">{t.upper(t('bodyProgressTitle'))}</h1>
        <button type="button" className="gm-btn gm-btn--soft" onClick={() => navigate('/progress/records')}><Icon name="history" /> {t('allRecords')}</button>
      </div>
      <ProgressView uid={user.id} programId={user.exerciseProgramID} />
      <div className="gm-fabs">
        <button type="button" className="gm-fab" onClick={() => setAdding(true)} title={t('progress')}>
          <Icon name="monitor_weight" /><span className="gm-fab__label">{t('addLogToday')}</span>
        </button>
      </div>
      {adding && <BodyEntryModal uid={user.id} onClose={() => setAdding(false)} />}
    </div>
  );
}

export function ProgressRecords() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const { bodyDays, loading } = useBodyDays(user.id);
  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <div className="gm-page-head">
        <button type="button" className="gm-icon-btn" onClick={() => navigate('/progress')} title={t('back')}><Icon name="arrow_back" /></button>
        <h1 className="gm-page-title">{t.upper(t('allRecords'))}</h1>
      </div>
      {loading ? <Loading /> : <BodyRecordsList uid={user.id} items={bodyDays} />}
    </div>
  );
}
